import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import Button from "../../../components/button/AdminActionsButton";
import ColorSelector from "../../../components/input/ColorSelector";
import Dropdown from "../../../components/input/Dropdown";
import InputfieldWithDropDown from "../../../components/input/InputfieldWithDropDown";
import AdminSidebar from "../../../components/sidebar/AdminSidebar";
import { usePopup } from "../../../context/PopupContext";
import { productService } from "../../../service/ProductService";
import { categories } from "../../../store/categoryStore";

// Styled components
const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Nav = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 10;

  @media (max-width: 1024px) {
    position: relative;
    height: auto;
    width: 100%;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  margin-left: 260px;
  width: calc(100% - 260px);

  @media (max-width: 1200px) {
    margin-left: 80px;
    width: calc(100% - 80px);
    padding: 15px;
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
    padding: 10px;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;

  @media (max-width: 768px) {
    padding: 20px 15px;
    border-radius: 8px;
  }
`;

const Title = styled.h1`
  text-align: center;
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 30px;
  color: #1a1a1a;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FormSection = styled.div`
  grid-column: ${(props) => (props.fullWidth ? "1 / -1" : "auto")};
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 15px;
  resize: vertical;
  min-height: 100px;
  transition:
    border 0.2s,
    box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #666;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const ButtonContainer = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FixedInputWrapper = styled.div`
  width: 100%;

  & > div {
    width: 100%;
  }

  input,
  select {
    width: 100%;
    box-sizing: border-box;
  }
`;

// New styled components for the image uploader
const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  margin-top: 10px;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 0, 0, 0.8);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;

  &:hover {
    background: rgba(255, 0, 0, 1);
  }
`;

const ImageUploadButton = styled.div`
  width: 120px;
  height: 120px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #666;
  }

  label {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
  }
`;

const PlusIcon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
  color: #666;
`;

const ImageUploadContainer = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-around;
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

function EditProductsPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { showPopup } = usePopup();
  const [product, setProduct] = useState();
  const [productData, setProductData] = useState({
    name: "",
    description: "",
    categoryName: "",
    gender: "",
    originalPrice: "",
    price: "",
    quantity: "",
    material: "",
    discount: "",
    sizes: [],
    colors: [],
    unchangedImages: [],
    deletedImages: [],
    newImages: [],
  });

  const handleInputChange = (field, value) => {
    setProductData((prev) => {
      let updatedData = { ...prev, [field]: value };

      // Calculate price based on original price and discount
      if (field === "originalPrice" || field === "discount") {
        const originalPrice = parseFloat(
          field === "originalPrice" ? value : prev.originalPrice,
        );
        const discount = parseFloat(
          field === "discount" ? value : prev.discount,
        );

        if (!isNaN(originalPrice)) {
          const discountedPrice = !isNaN(discount)
            ? originalPrice - (originalPrice * discount) / 100
            : originalPrice;
          updatedData.price = discountedPrice.toFixed(0);
        }
      }

      return updatedData;
    });
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete?"))
      productService.deleteProduct(productId, () => {
        showPopup("Product deleted successfully");
        navigate("/admin/products");
      });
  };

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      productService.getProductById(
        productId,
        (product) => {
          setProduct(product);
          setProductData({
            name: product.name || "",
            description: product.description || "",
            categoryName: product.category?.name || "",
            gender: product.gender || "Men",
            originalPrice: product.price / (1 - product.discount / 100) || "",
            price: product.price || "",
            quantity: product.quantity || "",
            material: product.material || "",
            discount: product.discount || 0,
            sizes: product.sizes?.map((s) => s.size) || [],
            colors: product.colors?.map((c) => c.name) || [],
            unchangedImages: product.images || [],
            deletedImages: [],
            newImages: [],
          });
        },
        {
          default: (message) => {
            alert(message);
          },
        },
      );
    };
    fetchProduct();
  }, [productId]);

  // Handle image deletion
  const handleDeleteImage = (imageId) => {
    setProductData((prev) => {
      const imageToDelete = prev.unchangedImages.find(
        (img) => img._id === imageId,
      );

      return {
        ...prev,
        unchangedImages: prev.unchangedImages.filter(
          (img) => img._id !== imageId,
        ),
        deletedImages: imageToDelete
          ? [...prev.deletedImages, imageToDelete._id]
          : prev.deletedImages,
      };
    });
  };

  // Handle new image upload
  const handleAddImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (
      productData.unchangedImages.length + productData.newImages.length >=
      6
    ) {
      alert("Maximum of 6 images allowed. Please delete an image first.");
      return;
    }

    // Check if file is an image
    if (!file.type.match("image.*")) {
      alert("Please select an image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const newImage = {
        file,
        preview: e.target.result,
        isNew: true,
      };

      setProductData((prev) => ({
        ...prev,
        newImages: [...prev.newImages, newImage],
      }));
    };
    reader.readAsDataURL(file);

    // Reset the file input
    e.target.value = "";
  };

  // Remove a new image that hasn't been uploaded yet
  const handleRemoveNewImage = (index) => {
    setProductData((prev) => ({
      ...prev,
      newImages: prev.newImages.filter((_, i) => i !== index),
    }));
  };

  const prepareFormData = () => {
    const formData = new FormData();

    // Add all fields individually
    Object.entries(productData).forEach(([key, value]) => {
      if (key === "sizes" || key === "colors") {
        // Append each array element with the same key
        value.forEach((item) => formData.append(key, item));
      } else if (key === "deletedImages") {
        // Append each deleted image ID
        value.forEach((id) => formData.append("deletedImages", id));
      } else if (key === "newImages") {
        // Append each new image file
        value.forEach((image) => {
          formData.append("newImages", image.file);
        });
      } else if (key !== "unchangedImages") {
        // Skip unchangedImages as we don't need to send them
        formData.append(key, value);
      }
    });

    return formData;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (
      productData.unchangedImages.length + productData.newImages.length ===
      0
    ) {
      showPopup("Error", "Please add at least one image.");
      return;
    }

    const formData = prepareFormData();

    // Debug what we're sending
    for (let [key, value] of formData.entries()) {
      // console.log(key, value);
    }

    productService.updateProduct(
      productId,
      formData,
      () => {
        showPopup(
          "Product updated successfully",
          "The product changes are saved.",
        );
        // navigate("/admin/products");
      },
      {
        default: (message) => {
          alert("Error", message);
        },
      },
    );
  };

  return (
    <Wrapper>
      <Nav>
        <AdminSidebar collapsed={window.innerWidth < 1200} />
      </Nav>
      <Content>
        <Container>
          <Title>Edit Product</Title>
          <Form onSubmit={handleSubmit}>
            <FormSection fullWidth>
              <Label>Product Images (Max 6)</Label>
              <ImageGrid>
                {/* Existing images */}
                {productData.unchangedImages.map((image) => (
                  <ImagePreview key={image._id}>
                    <img
                      src={process.env.REACT_APP_IMAGE_BASE_URL + image.path}
                      alt="Product"
                    />
                    <DeleteButton
                      type="button"
                      onClick={() => handleDeleteImage(image._id)}
                      aria-label="Delete image"
                    >
                      ×
                    </DeleteButton>
                  </ImagePreview>
                ))}

                {/* New images */}
                {productData.newImages.map((image, index) => (
                  <ImagePreview key={`new-${index}`}>
                    <img src={image.preview} alt="New upload" />
                    <DeleteButton
                      type="button"
                      onClick={() => handleRemoveNewImage(index)}
                      aria-label="Remove new image"
                    >
                      ×
                    </DeleteButton>
                  </ImagePreview>
                ))}

                {/* Add image button (only show if we have less than 6 images) */}
                {productData.unchangedImages.length +
                  productData.newImages.length <
                  6 && (
                  <ImageUploadButton>
                    <input
                      type="file"
                      accept=".png, .jpg, .jpeg"
                      onChange={handleAddImage}
                      style={{ display: "none" }}
                      id="image-upload"
                    />

                    <label htmlFor="image-upload">
                      <PlusIcon>+</PlusIcon>
                      <span>Add Image</span>
                    </label>
                  </ImageUploadButton>
                )}
              </ImageGrid>
            </FormSection>

            <FormSection fullWidth>
              <Label>Product Name</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="text"
                  value={productData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter product name"
                  required
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection fullWidth>
              <Label>Product Description</Label>
              <TextArea
                value={productData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Enter product description"
                required
              />
            </FormSection>

            <FormSection>
              <Label>Category</Label>
              <FixedInputWrapper>
                <Dropdown
                  type="dropdown"
                  value={productData.categoryName}
                  onChange={(value) => handleInputChange("categoryName", value)}
                  options={categories}
                  placeholder="Select or enter category"
                  required
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Gender</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="dropdown"
                  value={productData.gender}
                  onChange={(value) => handleInputChange("gender", value)}
                  options={["Men", "Women", "Unisex", "Kids"]}
                  placeholder="Select gender"
                  required
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Original Price ($)</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="number"
                  value={productData.originalPrice}
                  onChange={(e) =>
                    handleInputChange("originalPrice", e.target.value)
                  }
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Discount (%)</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="number"
                  value={productData.discount}
                  onChange={(e) =>
                    handleInputChange("discount", e.target.value)
                  }
                  placeholder="0-100"
                  min="0"
                  max="100"
                  required
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Final Price (after discount)</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="text"
                  value={productData.price}
                  onChange={(e) => handleInputChange("price", e.target.value)}
                  disabled
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Stock</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="number"
                  value={productData.quantity}
                  onChange={(e) =>
                    handleInputChange("quantity", e.target.value)
                  }
                  placeholder="Enter quantity"
                  min="0"
                  required
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Material</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="text"
                  value={productData.material}
                  onChange={(e) =>
                    handleInputChange("material", e.target.value)
                  }
                  placeholder="e.g., Cotton, Leather"
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection fullWidth>
              <Label>Available Sizes</Label>
              <ColorSelector
                selectedColors={productData.sizes}
                onChange={(sizes) => handleInputChange("sizes", sizes)}
              />
            </FormSection>

            <FormSection>
              <Label>Colors</Label>
              <ColorSelector
                selectedColors={productData.colors}
                onChange={(colors) => handleInputChange("colors", colors)}
              />
            </FormSection>

            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={handleDelete}>
                Delete Product
              </Button>
              <Button type="submit" variant="primary">
                Update Product
              </Button>
            </ButtonContainer>
          </Form>
        </Container>
      </Content>
    </Wrapper>
  );
}

export default EditProductsPage;
